/* Administration -> Webinar -> Public URL (2026-09 round 7).
 *
 * Shows the EXACT public base every webinar join / registration / attendance
 * callback URL is derived from (config key `public.base_url`), where it comes
 * from, and whether it is fit for a LIVE campaign (https + externally
 * reachable). An admin with webinar.config can set it; the server validates
 * it (https outside local dev, origin only, no IP / private host) and a LIVE
 * campaign cannot be activated while it is missing or insecure.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link2 } from "lucide-react";
import { toneClass } from "@/components/webinar/webinarShared";

const SOURCE_LABEL = {
  config: "Administration setting (public.base_url)",
  env: "Deployment environment variable (PUBLIC_BASE_URL)",
  dev_request: "Local development request origin",
};

export const WebinarPublicUrlCard = ({ publicUrl, canEdit, onSaved }) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { setValue(publicUrl?.configured_value || ""); }, [publicUrl?.configured_value]);
  if (!publicUrl) return null;

  const save = async (next) => {
    setBusy(true);
    setError(null);
    try {
      await api.put("/webinar/settings", { public_base_url: next });
      toast.success(next ? "Public base URL saved" : "Public base URL cleared");
      if (onSaved) onSaved();
    } catch (e) { setError(errDetail(e)); } finally { setBusy(false); }
  };

  const ok = publicUrl.base_url && publicUrl.valid && publicUrl.externally_valid;
  return (
    <Card data-testid="webinar-public-url-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Link2 className="h-4 w-4" /> Public URL — join links &amp; callbacks
          <Badge variant="outline" data-testid="webinar-public-url-status"
                 className={`text-[10px] ${toneClass(ok ? "success" : publicUrl.base_url ? "warning" : "danger")}`}>
            {ok ? "https · valid for LIVE" : publicUrl.base_url ? "local only — not valid for LIVE" : "not configured"}
          </Badge>
        </CardTitle>
        <p className="text-[11px] text-muted-foreground">
          Every URL below is derived from this one base — SalesOS never guesses it from the proxied
          request. https is required (plain http only for localhost in local development); the base
          is an origin only (no path, credentials or IP address). A LIVE campaign cannot be
          activated while it is missing or insecure.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        <div className="rounded-md border p-2 space-y-1" data-testid="webinar-public-url-effective">
          <p className="text-[11px] text-muted-foreground">
            Base in use{publicUrl.source ? ` — from ${SOURCE_LABEL[publicUrl.source] || publicUrl.source}` : ""}
          </p>
          {publicUrl.base_url ? (
            <code className="block text-[11px] break-all bg-muted rounded px-1.5 py-1" data-testid="webinar-public-url-base">
              {publicUrl.base_url}
            </code>
          ) : (
            <p className="text-[hsl(var(--danger))]" data-testid="webinar-public-url-missing">
              {(publicUrl.errors || []).join(" ") || "No public base URL is configured."}
            </p>
          )}
          {publicUrl.base_url && !publicUrl.valid && (publicUrl.errors || []).length > 0 && (
            <p className="text-[hsl(var(--danger))]">{publicUrl.errors.join(" ")}</p>
          )}
        </div>
        {publicUrl.join_base && (
          <div className="space-y-1" data-testid="webinar-public-url-derived">
            <p className="text-[11px] text-muted-foreground">Derived URLs</p>
            <p>Join link base (WhatsApp URL button):</p>
            <code className="block text-[11px] break-all bg-muted rounded px-1.5 py-1" data-testid="webinar-public-url-join">
              {publicUrl.join_base}
            </code>
            {Object.entries(publicUrl.attendance_webhooks || {}).map(([p, u]) => (
              <p key={p} className="text-[11px] break-all" data-testid={`webinar-public-url-webhook-${p}`}>
                <span className="text-muted-foreground">{p} attendance webhook:</span> {u}
              </p>
            ))}
          </div>
        )}
        {canEdit && (
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[260px] space-y-1">
              <label className="text-[11px] text-muted-foreground" htmlFor="webinar-public-url-input">
                Public base URL (https://…)
              </label>
              <Input id="webinar-public-url-input" className="h-8 text-xs font-mono"
                     data-testid="webinar-public-url-input" value={value} placeholder="https://app.example.com"
                     onChange={(e) => { setValue(e.target.value); setError(null); }} />
            </div>
            <Button size="sm" className="h-8 text-xs" disabled={busy || !value.trim()}
                    data-testid="webinar-public-url-save" onClick={() => save(value.trim())}>Save</Button>
            {publicUrl.configured_value && (
              <Button size="sm" variant="ghost" className="h-8 text-xs" disabled={busy}
                      data-testid="webinar-public-url-clear" onClick={() => save("")}>Clear</Button>
            )}
          </div>
        )}
        {error && <p className="text-[hsl(var(--danger))]" data-testid="webinar-public-url-error">{error}</p>}
      </CardContent>
    </Card>
  );
};

export default WebinarPublicUrlCard;
